import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { isBound, type RepoBinding } from '../lib/repo-bindings';
import { Panel } from './Panel';
import { Button } from './ui/Button';

// The user-token repo-binding control (arc 101 slice 05). Presenter: lists the
// connected token's allowlisted repos, each with whether developerz.ai drives it
// as the user, and a Bind / Unbind toggle. `busyRepo` is the one row in flight;
// `error` is the API's own refusal sentence. All async state comes from the route.
export type GithubRepoBindingsProps = {
  /** The token's allowlist (`GET /v1/me/github-token` `repos`). */
  readonly repos: readonly string[];
  readonly bindings: readonly RepoBinding[];
  readonly busyRepo: string | null;
  readonly error: string | null;
  readonly onBind: (repo: string) => void;
  readonly onUnbind: (repo: string) => void;
};

export function GithubRepoBindings(props: GithubRepoBindingsProps): JSX.Element {
  return (
    <Panel title={t('github_token.bindings_title')} class="github-token">
      <p class="github-token__lead">{t('github_token.bindings_lead')}</p>
      <Show when={props.error}>
        {(msg) => (
          <p class="field__error" role="alert">
            {msg()}
          </p>
        )}
      </Show>
      <ul class="github-token__repos">
        <For each={props.repos}>
          {(repo) => {
            const bound = () => isBound(props.bindings, repo);
            const busy = () => props.busyRepo === repo;
            return (
              <li class="github-token__binding">
                <code>{repo}</code>
                <span class="github-token__binding-state">
                  {t(bound() ? 'github_token.binding_bound' : 'github_token.binding_unbound')}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={props.busyRepo !== null}
                  aria-label={t(bound() ? 'github_token.unbind_aria' : 'github_token.bind_aria', {
                    repo,
                  })}
                  onClick={() => (bound() ? props.onUnbind(repo) : props.onBind(repo))}
                >
                  {t(
                    busy()
                      ? 'github_token.binding_busy'
                      : bound()
                        ? 'github_token.unbind'
                        : 'github_token.bind',
                  )}
                </Button>
              </li>
            );
          }}
        </For>
      </ul>
    </Panel>
  );
}
